import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import {
  RectangleGroupIcon,
  VideoCameraIcon,
  LinkIcon,
  PencilSquareIcon,
  CameraIcon,
  CloudArrowUpIcon,
  BoltIcon,
  ShieldCheckIcon,
  RectangleStackIcon,
  SwatchIcon,
  EyeSlashIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline"

// ---------------------------------------------------------------------------
// Primary features
// ---------------------------------------------------------------------------
// Built inside the component (not module-level) since titles/text come
// from the CMS texts bundle.
function getFeatures(texts) {
  return [
    {
      icon: RectangleGroupIcon,
      title: texts?.feature_1_title,
      text: texts?.feature_1_text,
    },
    {
      icon: VideoCameraIcon,
      title: texts?.feature_2_title,
      text: texts?.feature_2_text,
    },
    {
      icon: LinkIcon,
      title: texts?.feature_3_title,
      text: texts?.feature_3_text,
    },
    {
      icon: PencilSquareIcon,
      title: texts?.feature_4_title,
      text: texts?.feature_4_text,
    },
    {
      icon: CameraIcon,
      title: texts?.feature_5_title,
      text: texts?.feature_5_text,
    },
    {
      icon: CloudArrowUpIcon,
      title: texts?.feature_6_title,
      text: texts?.feature_6_text,
    },
    {
      icon: BoltIcon,
      title: texts?.feature_7_title,
      text: texts?.feature_7_text,
    },
    {
      icon: ShieldCheckIcon,
      title: texts?.feature_8_title,
      text: texts?.feature_8_text,
    },
    {
      icon: RectangleStackIcon,
      title: texts?.feature_9_title,
      text: texts?.feature_9_text,
    },
    {
      icon: EyeSlashIcon,
      title: texts?.feature_10_title,
      text: texts?.feature_10_text,
    },
    {
      icon: ArrowsRightLeftIcon,
      title: texts?.feature_11_title,
      text: texts?.feature_11_text,
    },
    {
      icon: SwatchIcon,
      title: texts?.feature_12_title,
      text: texts?.feature_12_text,
    },
  ]
}

const FEATURE_COLORS = [
  "accent",
  "accent_1",
  "accent_2",
  "accent_3",
  "accent_4",
  "accent_5",
  "accent_6",
  "accent_7",
]

// ---------------------------------------------------------------------------
// Expand/shrink mechanics — ported from AffiliateSection2's CardColumn:
// hovering a card expands it and shrinks its column siblings; every other
// column is untouched. Heights are fixed pixel targets (not measured), same
// approach as the source component.
// ---------------------------------------------------------------------------
const COLLAPSED_HEIGHT = 156 // icon + title only (default)
const EXPANDED_HEIGHT = 280 // icon + title + text (hovered card)
const EXPAND_EASING = "cubic-bezier(0.34, 1.56, 0.64, 1)"

// Shrunk siblings absorb exactly the height the expanded card gained, so a
// column's total stays equal to every other (untouched) column's total —
// N * COLLAPSED_HEIGHT === EXPANDED_HEIGHT + (N - 1) * shrunkHeight.
const shrunkHeightFor = (columnLength) =>
  columnLength > 1
    ? (columnLength * COLLAPSED_HEIGHT - EXPANDED_HEIGHT) / (columnLength - 1)
    : COLLAPSED_HEIGHT

const fadeInKeyframes = `
  @keyframes featureFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`

// Devices with no hover (touch) can never trigger the expand-on-hover
// interaction, so tapping is used there instead (see activeGlobalIndex).
const useHasHover = () => {
  const [hasHover, setHasHover] = useState(true)
  useEffect(() => {
    const mq = window.matchMedia("(hover: hover)")
    const update = () => setHasHover(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])
  return hasHover
}

// Groups the visible column count switches with the grid's own Tailwind
// breakpoints (grid-cols-1 / sm:grid-cols-2 / lg:grid-cols-4) so a card's
// siblings-within-its-column always match what's visually stacked above/
// below it.
const useColumnCount = () => {
  const [columnCount, setColumnCount] = useState(1)
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth
      setColumnCount(w >= 1024 ? 4 : w >= 640 ? 2 : 1)
    }
    calc()
    window.addEventListener("resize", calc)
    return () => window.removeEventListener("resize", calc)
  }, [])
  return columnCount
}

const FeatureCard = ({ feature, colorKey, isExpanded, isShrunk, shrunkHeight }) => {
  const shadowVar = `rgb(var(--color-${colorKey}) / 0.2)`
  const height = isExpanded ? EXPANDED_HEIGHT : isShrunk ? shrunkHeight : COLLAPSED_HEIGHT

  return (
    <div
      className={`group relative bg-paper border ${isExpanded ? "grad_border_6" : "grad_border_1"} hover:grad_border_6 rounded-2xl p-6 overflow-hidden transition-shadow duration-300 shadow-md hover:shadow-[0_8px_16px_-1px_var(--card-sc),0_8px_16px_-2px_var(--card-sc)]`}
      style={{
        height: `${height}px`,
        "--card-sc": shadowVar,
        transitionProperty: "height, box-shadow",
        transitionTimingFunction: EXPAND_EASING,
        transitionDuration: "600ms",
      }}
      onMouseMove={e => {
        const r = e.currentTarget.getBoundingClientRect()
        e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`)
        e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`)
      }}
    >
      <div className={`h-full flex flex-col gap-4 ${isShrunk ? "justify-center" : "justify-start"}`}>
        {/* Icon — no round backdrop, sized up, tinted text-secondary. Hidden
            in the shrunk state to leave room for the centered title. */}
        {!isShrunk && (
          <feature.icon className={`relative z-10 w-10 h-10 flex-shrink-0 ${isExpanded ? "text-primary" : "text-secondary"}`} />
        )}

        <Typography
          variant="body1_accent"
          className={`relative z-10 text-text leading-tight my-0 ${isShrunk ? "text-center" : ""}`}
        >
          {feature.title}
        </Typography>

        {isExpanded && (
          <Typography
            variant="txt_dim"
            className="relative z-10 leading-normal opacity-0"
            style={{ animation: `featureFadeIn 600ms ${EXPAND_EASING} forwards 150ms` }}
          >
            {feature.text}
          </Typography>
        )}
      </div>
    </div>
  )
}

// One vertical stack. On hover-capable devices it owns its own hovered
// card, independent of every other column (desktop behavior). On no-hover
// (touch) devices there is no per-column state at all — which single card
// is expanded is tracked globally (activeGlobalIndex, owned by the parent
// section) so tapping any card anywhere always leaves exactly one expanded.
const FeatureColumn = ({ items, hasHover, activeGlobalIndex, setActiveGlobalIndex }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const shrunkHeight = shrunkHeightFor(items.length)
  const activeIndex = hasHover
    ? hoveredIndex
    : items.findIndex(({ i }) => i === activeGlobalIndex)

  return (
    <div className="flex flex-col gap-6">
      {items.map(({ feature, colorKey, i }, index) => (
        <div
          key={feature.title}
          onMouseEnter={() => hasHover && setHoveredIndex(index)}
          onMouseLeave={() => hasHover && setHoveredIndex(null)}
          onClick={() => !hasHover && setActiveGlobalIndex(i)}
        >
          <FeatureCard
            feature={feature}
            colorKey={colorKey}
            isExpanded={activeIndex === index}
            isShrunk={activeIndex !== -1 && activeIndex !== null && activeIndex !== index}
            shrunkHeight={shrunkHeight}
          />
        </div>
      ))}
    </div>
  )
}

export default function FeaturesSection() {
  const texts = TextBundle({ id: "home_features_section" })
  const features = getFeatures(texts)
  const columnCount = useColumnCount()
  const hasHover = useHasHover()
  // Only meaningful on no-hover devices — the single globally-expanded
  // card. Defaults to the very first feature.
  const [activeGlobalIndex, setActiveGlobalIndex] = useState(0)

  const featuresWithMeta = features.map((feature, i) => ({
    feature,
    colorKey: FEATURE_COLORS[i % FEATURE_COLORS.length],
    i,
  }))
  const columns = Array.from({ length: columnCount }, (_, c) =>
    featuresWithMeta.filter((f) => f.i % columnCount === c)
  )

  return (
    <section className="px-4 pt-40 md:pt-52 pb-20 md:pb-28" style={{ overflowX: "clip" }}>
      <div className="max-w-4xl lg:max-w-7xl mx-auto">
      <div className="text-center mb-14">
        <Typography
          component="h2"
          variant="h1"
        >
          {texts?.section_title}
        </Typography>

        <Typography
          variant="body1_dim"
          className="mt-2 mb-0"
        >
          {texts?.section_subtitle}
        </Typography>
      </div>

      <style>{fadeInKeyframes}</style>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
        {columns.map((items, c) => (
          <FeatureColumn
            key={c}
            items={items}
            hasHover={hasHover}
            activeGlobalIndex={activeGlobalIndex}
            setActiveGlobalIndex={setActiveGlobalIndex}
          />
        ))}
      </div>
      </div>
    </section>
  )
}
